import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/api/queryClient";
import App from "./App";
import "./index.css";
import { installNumberInputWheelGuard } from "@/utils/numberInputWheelGuard";
import { installExpiredFileLinkGuard } from "@/utils/expiredFileLinkGuard";
import {
  PUBLIC_PATH_PREFIX,
  isPublicHost,
  portalUrl,
} from "@/features/rfq/publicSite";

// Guest subdomain (meets.elgw5.com) serves ONLY the public /meet/{token}
// pages — any other path bounces to the main app domain so the guest host
// never renders the authenticated shell.
if (
  window.location.hostname.startsWith("meets.") &&
  !window.location.pathname.startsWith("/meet/")
) {
  const mainHost = window.location.hostname.replace(/^meets\./, "");
  window.location.replace(`${window.location.protocol}//${mainHost}/`);
}

// Publiczna subdomena postępowań (postepowania.elgw5.com, lokalnie
// postepowania.localhost:5173) serwuje TĘ SAMĄ aplikację, ale pokazuje WYŁĄCZNIE
// witrynę — także zalogowanemu. Kto chce do klasycznego portalu, klika przycisk na
// stronie; wszystko poza /postepowania odbijamy na domenę główną (jak meets.*).
//
// UWAGA: to musi być PRAWDZIWE przejście, nie `history.replaceState`. Router
// powstaje przy imporcie modułu `App` (createBrowserRouter czyta adres w chwili
// tworzenia), a importy wykonują się PRZED tym kodem — podmieniony po cichu adres
// byłby już spóźniony i router pokazałby pulpit (czyli ekran logowania).
if (isPublicHost()) {
  const { pathname, search, origin } = window.location;
  if (pathname === "/") {
    // Goły adres subdomeny = witryna, nie pulpit.
    window.location.replace(`${origin}${PUBLIC_PATH_PREFIX}`);
  } else if (!pathname.startsWith(PUBLIC_PATH_PREFIX)) {
    // Cokolwiek innego należy do klasycznego portalu — witryna nigdy nie renderuje
    // zalogowanej powłoki, nawet komuś, kto ma tu ważną sesję.
    window.location.replace(portalUrl(pathname + search));
  }
}

// Dropping a file outside an upload zone would otherwise navigate the tab
// to the file and wipe all in-progress form state. Block it globally;
// drop zones call preventDefault themselves before the event bubbles here,
// and their React onDrop handlers run regardless of these listeners.
window.addEventListener("dragover", (e) => e.preventDefault());
window.addEventListener("drop", (e) => e.preventDefault());

// A stray wheel over a focused number field must never edit it — see the module.
installNumberInputWheelGuard();

// A file link that expired while the tab sat open says "refresh the page" instead of
// opening S3's raw "Request has expired" error — see the module.
installExpiredFileLinkGuard();

// After a deploy, a long-lived tab's lazy-route chunks 404 (hashed filenames
// rotated) — without this the user gets a blank pane and, worse, keeps running
// pre-deploy code next to peers on the new version. One reload heals both.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  // At most one reload per minute: if index.html itself is served stale (proxy
  // cache), the fresh page hits the same missing chunk and would loop forever.
  const KEY = "elg5:chunk-reload-at";
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 60_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);
